"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import appleStoreIcon from "@/app/assets/images/icm-3.0/apple-store.png";
import playStoreIcon from "@/app/assets/images/icm-3.0/play-store.png";
import Facebook from "@/app/assets/icons/facebook";
import Instagram from "@/app/assets/icons/instagram";
import LinkedIn from "@/app/assets/icons/linkedin";
import X from "@/app/assets/icons/x";
import Youtube from "@/app/assets/icons/youtube";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import {
    BSE,
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    NSE,
    SEBI,
    X_TWITTER,
    YOUTUBE,
} from "@/app/utils/constants";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import useMobile from "@/app/hooks/use-mobile";
import { useAuth } from "@/context/authcontext";
import { InvestmentOptions } from "@/app/interface/platform";
import useNavItems from "@/app/hooks/nav-items.hook";
import { getActiveNavLogoUrl } from "@/app/utils/get-nav-logo-url";
import { cn } from "@/lib/utils";

/** From old footer commented Important Links row — NSDL not exported in constants. */
const NSDL = "https://nsdl.co.in/";

interface FooterProps {
    active?: string;
}

type FooterLinkItem = {
    url: string;
    heading: string;
    events?: string;
    target?: string;
    external?: boolean;
    /** Render as text only when no verified URL exists */
    unlinked?: boolean;
};

function isInternalHref(href: string, target?: string) {
    if (target === "_blank") return false;
    if (!href || href === "#") return false;
    if (href.startsWith("/")) return true;
    try {
        const url = new URL(href, "https://incredmoney.com");
        return url.hostname.includes("incredmoney");
    } catch {
        return false;
    }
}

export const Footer: React.FC<FooterProps> = ({ active = InvestmentOptions.Home }) => {
    const pathName = usePathname();
    const { isAuthenticated } = useAuth();
    const navItems = useNavItems({ active, isLoggedIn: Boolean(isAuthenticated) });
    const { isMobile } = useMobile();

    const [isInvestNowSection, setIsInvestNowSection] = useState(false);

    const productLinks: FooterLinkItem[] = [
        // { url: brokingUrl, heading: "Stocks", events: "Broking_Footer" },
        // { url: brokingUrl, heading: "F&O", events: "Broking_Footer" },
        // {
        //     url: `${isAuthenticated ? process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL + DirectPaths.US_ETFS : process.env.ICM_GLOBAL_URL + DirectPaths.US_ETFS}`,
        //     heading: "ETF",
        // },
        { url: `${process.env.ICM_URL}/indices`, target: "_blank", external: true, heading: "Indices", events: "Broking_Footer" },
        // { url: brokingUrl, heading: "IPO", events: "Broking_Footer" },
        {
            url: `${isAuthenticated ? process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL + DirectPaths.US_STOCKS : process.env.ICM_GLOBAL_URL + DirectPaths.US_STOCKS}`,
            heading: "US Stocks",
            events: "US_Stocks_Footer",
        },
        {
            url:
                `${isAuthenticated ? process.env.ICM_BULLION_POST_URL : process.env.ICM_BULLION_URL}${DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.GOLD}` ||
                "#",
            heading: "Digital Gold",
        },
        {
            url:
                `${isAuthenticated ? process.env.ICM_BULLION_POST_URL : process.env.ICM_BULLION_URL}${DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.SILVER}` ||
                "#",
            heading: "Digital Silver",
        },
        // Old Products — not in Figma Products column
        { url: `${process.env.ICM_BONDS_URL}/login` || "#", heading: "Bonds" },
        {
            url:
                (isAuthenticated ? `${process.env.ICM_FD_POST_URL}` : `${process.env.ICM_FD_URL}`) +
                    DirectPaths.FD_POSTFIX_PATH || "#",
            heading: "FDs",
            events: "FDs_Footer",
        },
        { url: `${process.env.ICM_BONDS_URL}/login` || "#", heading: "MLDs" },
    ];

    const socialLinks = [
        { url: FACEBOOK, icon: <Facebook className="size-6 text-nav-text" />, label: "Facebook" },
        {
            url: INSTAGRAM,
            icon: <Instagram className="size-6 text-nav-text" />,
            label: "Instagram",
        },
        { url: LINKEDIN, icon: <LinkedIn className="size-6 text-nav-text" />, label: "LinkedIn" },
        { url: X_TWITTER, icon: <X className="size-6 text-nav-text" />, label: "X" },
        { url: YOUTUBE, icon: <Youtube className="size-6 text-nav-text" />, label: "YouTube" },
    ];

    const incredMoneyLinks: FooterLinkItem[] = [
        // { url: openDematHref, heading: "Open Demat Account" },
        {
            url: `${process.env.ICM_KNOWLEDGE_CENTRE}` || "#",
            heading: "Knowledge Center",
            target: "_blank",
            external: true,
        },
        {
            url: `${process.env.ICM_LEARN}` || "#",
            heading: "Blogs",
            target: "_blank",
            external: true,
        },
        {
            url: process.env.ICM_MEDIA || "#",
            heading: "News",
            target: "_blank",
            external: true,
        },
        { url: process.env.ICM_ABOUT || "#", heading: "About us" },
        { url: process.env.ICM_CAREERS || "#", heading: "Career" },
        {
            url: `${process.env.ICM_URL}${DirectPaths.CONTACT}` || "#",
            heading: "Contact us",
        },
        // { url: "#", heading: "Help & Support", unlinked: true },
    ];

    const importantLinks: FooterLinkItem[] = [
        {
            url: `${process.env.ICM_URL}${DirectPaths.ANNOUNCEMENT}` || "#",
            heading: "Important Announcement",
        },
        {
            url: `${process.env.ICM_URL}${DirectPaths.PRIVACY_POLICY}` || "#",
            heading: "Privacy Policy",
        },
        {
            url: `${process.env.ICM_URL}${DirectPaths.INVESTOR_RELATIONS}` || "#",
            heading: "Investor Relations",
        },
        {
            url:
                active === InvestmentOptions.UnlistedShares || active === InvestmentOptions.Refer
                    ? `${process.env.ICM_URL}${DirectPaths.UE_DISCLAIMER}` || "#"
                    : `${process.env.ICM_URL}${DirectPaths.DISCLAIMER}` || "#",
            heading: "Disclaimer",
        },
        {
            url: `${process.env.ICM_URL}${DirectPaths.POLICY_CENTER}` || "#",
            heading: "Legal & Regulatory",
        },
        // Old Quick Link — not in Figma Important Links
        {
            url: `${process.env.ICM_URL}${DirectPaths.TERMS_AND_CONDITIONS}` || "#",
            heading: "Terms & Conditions",
        },
    ];

    const regulatoryLinks = [
        { url: SEBI, heading: "SEBI" },
        { url: BSE, heading: "BSE" },
        { url: NSE, heading: "NSE" },
        { url: NSDL, heading: "NSDL" },
    ];

    const getWhiteLogo = (url: string) => {
        const original = url.substring(url.lastIndexOf("/"));
        return url.replace(original, "/white" + original);
    };

    const logoUrl = useMemo(
        () =>
            getActiveNavLogoUrl(
                navItems,
                getWhiteLogo(String(process.env.ICM_LOGO_URL ?? "")),
                getWhiteLogo,
            ),
        [navItems],
    );

    const renderLinkList = (section: FooterLinkItem[]) => (
        <>
            {section.map((link) => {
                if (
                    link.heading === "US Stocks" &&
                    process.env.SHOW_GLOBAL_STOCKS !== "true"
                ) {
                    return null;
                }

                const className =
                    "text-base font-regular leading-[22px] text-neutralOpacityLight-50 hover:text-nav-text";

                if (link.unlinked) {
                    return (
                        <span key={link.heading} className={className}>
                            {link.heading}
                        </span>
                    );
                }

                const onClick = () => setAnalyticsEvent(link.url, link.events || "");

                if (isInternalHref(link.url, link.target) && !link.external) {
                    return (
                        <Link
                            key={link.heading}
                            href={link.url || "/"}
                            className={className}
                            onClick={onClick}
                        >
                            {link.heading}
                        </Link>
                    );
                }

                return (
                    <a
                        key={link.heading}
                        href={link.url}
                        target={link.target ?? undefined}
                        rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                        className={className}
                        onClick={onClick}
                    >
                        {link.heading}
                    </a>
                );
            })}
        </>
    );

    useEffect(() => {
        if (
            isMobile &&
            (pathName.includes("/unlisted-shares") ||
                pathName.includes("/refer") ||
                pathName.includes("/bullion"))
        ) {
            setIsInvestNowSection(true);
        } else {
            setIsInvestNowSection(false);
        }
    }, [isMobile, pathName]);

    const disclaimerBroking = useMemo(
        () => (
            <>
                <p className="text-sm font-medium leading-[19.6px]">
                    <b>InCred Money Broking Limited</b> is a SEBI registered Stock Broker having
                    SEBI Registration No. INZ000164738. Also member of NSE (Member Code 09073), BSE
                    (Member Code 6329), MCX (Member Code : 55215), NCDEX (Member Code : 1233) and
                    Depository Participant with NSDL (IN-DP-474-2020).
                </p>
                <br />
                <p className="text-sm font-medium leading-[19.6px]">
                    <b>
                        Broking and DP services are offered by (InCred Money Broking Limited
                        (formerly known as South Asian Stocks Limited).
                    </b>{" "}
                    Transactions in listed securities (Cash and FO) are executed on the platform :
                    <a
                        href="https://incredstocko.in/"
                        target="_blank"
                        className="ml-1 underline"
                        rel="noopener noreferrer"
                    >
                        https://incredstocko.in/
                    </a>
                </p>
                <br />
                <p className="text-sm font-medium leading-[19.6px]">
                    <i>
                        Investments in securities market are subject to market risks, read all the
                        related documents carefully before investing.
                    </i>
                </p>
            </>
        ),
        [],
    );

    const linkColumnClass = "flex flex-col gap-10";
    const linkListClass = "flex flex-col gap-s9";
    const columnTitleClass = "text-lg font-semibold leading-6 text-nav-text";
    const mobileColumnTitleClass = "text-base font-semibold text-white";
    const mobileLinkListClass = "flex flex-col gap-3";
    const mobileSectionClass = "flex flex-col gap-5";

    return (
        <footer
            className={cn(
                "w-full self-stretch bg-nav-bg px-4 py-[60px] md:px-[120px] md:py-[100px]",
                isInvestNowSection && "pb-16",
            )}
        >
            <div className="mx-auto flex w-full max-w-[1160px] flex-col gap-s9">
                {/* Brand + link columns */}
                <div className="flex flex-col gap-10 pb-9 md:flex-row md:items-start md:justify-center md:gap-[50px]">
                    <div className="flex w-full shrink-0 flex-col gap-10 md:w-[284px]">
                        <Image src={logoUrl} alt="InCred Logo" width={140} height={49} />

                        <p className="text-base font-regular leading-[22px] text-neutralOpacityLight-50">
                            ⓒ2022-2026 InCred Money,
                            <br />
                            Built with love in India.
                            <br />
                            <br />
                            All rights reserved.
                        </p>

                        <div className="flex items-center gap-s9">
                            {socialLinks.map((link) => (
                                <a
                                    key={link.label}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    className="flex size-6 items-center justify-center"
                                    onClick={() => setAnalyticsEvent(link.url, "")}
                                >
                                    {link.icon}
                                </a>
                            ))}
                        </div>

                        <div className="flex w-full items-start gap-7">
                            <a
                                href={process.env.ICM_APPLE_APP_URL || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-[42px] w-[128px] shrink-0 items-center gap-2 overflow-hidden rounded-[var(--radius-m)] bg-white px-2 py-1"
                            >
                                <span
                                    className="relative h-[22px] w-[18px] shrink-0 overflow-hidden"
                                    aria-hidden
                                >
                                    <Image
                                        src={appleStoreIcon}
                                        alt=""
                                        width={38}
                                        height={38}
                                        className="absolute left-[-55.56%] top-[-36.36%] h-[172.73%] w-[211.11%] max-w-none"
                                    />
                                </span>
                                <span className="flex w-[81px] flex-col font-medium text-ink-body">
                                    <span className="text-2xs leading-[14px]">Download on the</span>
                                    <span className="text-sm leading-5">App Store</span>
                                </span>
                            </a>
                            <a
                                href={process.env.ICM_PLAYSTORE_URL || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-[42px] w-[128px] shrink-0 items-center gap-2 overflow-hidden rounded-[var(--radius-m)] bg-white px-2 py-1"
                            >
                                <span
                                    className="relative size-[22px] shrink-0 overflow-hidden"
                                    aria-hidden
                                >
                                    <Image
                                        src={playStoreIcon}
                                        alt=""
                                        width={22}
                                        height={22}
                                        className="size-full object-cover"
                                    />
                                </span>
                                <span className="flex w-[81px] flex-col font-medium text-ink-body">
                                    <span className="text-2xs leading-[14px]">Download on the</span>
                                    <span className="text-sm leading-5">Play Store</span>
                                </span>
                            </a>
                        </div>

                        {/* {active === InvestmentOptions.Gold ||
                        active === InvestmentOptions.Bullion ||
                        active === InvestmentOptions.Home ? (
                            <div className="flex items-center gap-2">
                                <Image
                                    src={
                                        process.env.ICM_PUBLIC_ASSETS_URL +
                                        "images/v2/webp/MMTC-PAMP-Logo.webp"
                                    }
                                    alt=""
                                    width={50}
                                    height={50}
                                />
                                <span className="text-sm text-neutralOpacityLight-50">
                                    Digital Gold/Silver on InCred Money powered by MMTC-PAMP
                                </span>
                            </div>
                        ) : null} */}
                    </div>

                    {/* Mobile link columns: 2 cols with vertical divider */}
                    <div className="flex gap-4 md:hidden">
                        <div className="flex flex-1 flex-col gap-8">
                            <div className={mobileSectionClass}>
                                <h5 className={mobileColumnTitleClass}>InCred Money</h5>
                                <div className={mobileLinkListClass}>
                                    {renderLinkList(incredMoneyLinks)}
                                </div>
                            </div>
                            {/* <div className={mobileSectionClass}>
                                <h5 className={mobileColumnTitleClass}>Platform & Tools</h5>
                                <div className={mobileLinkListClass}>
                                    {renderLinkList(platformLinks)}
                                </div>
                            </div> */}
                        </div>
                        <div className="w-px shrink-0 self-stretch bg-nav-stroke" aria-hidden />
                        <div className="flex flex-1 flex-col gap-8">
                            <div className={mobileSectionClass}>
                                <h5 className={mobileColumnTitleClass}>Products</h5>
                                <div className={mobileLinkListClass}>
                                    {renderLinkList(productLinks)}
                                </div>
                            </div>
                            <div className={mobileSectionClass}>
                                <h5 className={mobileColumnTitleClass}>Important Links</h5>
                                <div className={mobileLinkListClass}>
                                    {renderLinkList(importantLinks)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop link columns: 4-col layout */}
                    <div className="hidden flex-1 md:flex md:flex-row md:flex-wrap md:justify-between md:gap-0">
                        <div className={linkColumnClass}>
                            <h5 className={columnTitleClass}>InCred Money</h5>
                            <div className={linkListClass}>{renderLinkList(incredMoneyLinks)}</div>
                        </div>
                        <div className={cn(linkColumnClass, "md:w-[172px]")}>
                            <h5 className={columnTitleClass}>Products</h5>
                            <div className={linkListClass}>{renderLinkList(productLinks)}</div>
                        </div>
                        {/* <div className={cn(linkColumnClass, "md:w-[172px]")}>
                            <h5 className={columnTitleClass}>Platform & Tools</h5>
                            <div className={linkListClass}>{renderLinkList(platformLinks)}</div>
                        </div> */}
                        <div className={linkColumnClass}>
                            <h5 className={columnTitleClass}>Important Links</h5>
                            <div className={linkListClass}>{renderLinkList(importantLinks)}</div>
                        </div>
                    </div>
                </div>

                {/* SEO directory — mobile accordion */}
                {/* <div className="w-full md:hidden">
                    <Accordion
                        type="single"
                        collapsible
                        defaultValue="Share Market"
                        className="w-full"
                    >
                        {SEO_TABS.map((tab) => (
                            <AccordionItem
                                key={tab}
                                value={tab}
                                className="border-0 border-t border-solid border-nav-stroke"
                            >
                                <AccordionTrigger
                                    showIcon={true}
                                    className="py-5 text-left text-lg font-semibold text-white hover:no-underline"
                                >
                                    {tab === "Indicies" ? "Indices" : tab}
                                </AccordionTrigger>
                                <AccordionContent className="pb-5">
                                    {renderSeoTabContent(tab)}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div> */}

                {/* SEO directory — desktop tabs + panels */}
                {/* <div className="hidden w-full flex-col gap-8 md:flex">
                    <div className="hidden w-full flex-col gap-s9 md:flex">
                        <div className="flex w-full items-start justify-between overflow-x-auto border-b border-nav-stroke">
                            {SEO_TABS.map((tab) => {
                                const isActive = activeSeoTab === tab;
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveSeoTab(tab)}
                                        className={cn(
                                            "relative h-[30px] shrink-0 whitespace-nowrap px-2.5 py-1.5 text-base font-medium leading-[22px]",
                                            isActive
                                                ? "text-cta-primary-text"
                                                : "text-ink-label",
                                        )}
                                    >
                                        {tab}
                                        {isActive ? (
                                            <span className="absolute inset-x-0 bottom-0 h-0.5 bg-cta-primary-text" />
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="hidden md:block">{renderSeoTabContent(activeSeoTab)}</div>
                    </div>
                </div> */}

                {/* Stocks directory + disclaimer */}
                <div className="flex w-full flex-col gap-s9">
                    <div className="hidden h-px w-full bg-nav-stroke md:block" />

                    {/* <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center">
                        <p className="shrink-0 text-base font-semibold leading-[22px] text-white">
                            Stocks directory:
                        </p>
                        <div className="flex flex-1 flex-wrap items-center justify-between gap-y-2">
                            {STOCKS_DIRECTORY.map((letter, index) => (
                                <React.Fragment key={letter}>
                                    {index > 0 ? (
                                        <span
                                            aria-hidden
                                            className="hidden h-5 w-px bg-nav-stroke md:block"
                                        />
                                    ) : null}
                                    <span className="inline-flex h-8 min-w-7 items-center justify-center px-2 text-lg font-medium leading-6 text-neutralOpacityLight-50">
                                        {letter}
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    <div className="h-px w-full bg-nav-stroke" /> */}

                    <Accordion type="single" collapsible={true} className="w-full max-w-full pt-5">
                        <AccordionItem value={"Disclaimer"} className="border-none">
                            <AccordionTrigger
                                showIcon={true}
                                className="py-0 text-left text-lg font-semibold leading-6 text-nav-text hover:no-underline"
                            >
                                Disclaimer
                            </AccordionTrigger>
                            <AccordionContent className="pt-[15px] text-justify text-sm font-regular leading-5 text-neutralOpacityLight-50">
                                {active === InvestmentOptions.Broking && (
                                    <div className="mb-5 pb-5">{disclaimerBroking}</div>
                                )}
                                {active === InvestmentOptions.Home ||
                                active === "Calculator" ||
                                active === InvestmentOptions.Global ? (
                                    <div>
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            Fixed Deposits are offered by Banks/NBFCs and are
                                            regulated by the Reserve Bank of India. ETA Fintech
                                            Private Limited, under the brand name of InCred Money,
                                            is acting in the capacity of Sub Agent/Referral Partner
                                            for distribution of Fixed Deposits of Banks and NBFCs.
                                            The funds transferred by the customer for the Fixed
                                            Deposits are directly credited to the regulated entity
                                            offering the Fixed Deposit product. Fixed Deposits are
                                            offered to Resident Indians above the age of 18 who have
                                            a PAN & an Aadhaar. Interest rates are subject to
                                            change.{" "}
                                            <a
                                                href={process.env.TERMS}
                                                target="_blank"
                                                className="underline"
                                                rel="noopener noreferrer"
                                            >
                                                *T&C apply.
                                            </a>{" "}
                                        </p>
                                        <br />
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            *Corporate Bonds transactions are executed on
                                            Biddeasy.com. Biddeasy is Online Bond platform provider
                                            and registered with BSE and NSE in the name of Oro
                                            Financial Consultants Pvt Ltd.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            Oro Financial Consultants Pvt Ltd is the Stock broker
                                            entity operating in cash and debt segment with BSE and
                                            Debt segment with NSE having SEBI registration number:
                                            INZ000312534, Registered as an Online Bond Platform
                                            Provider in BSE Ltd.(Member Code: 6821) and National
                                            Stock Exchange of India Limited (Member Code: 90389){" "}
                                        </p>
                                        <br />
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {active === InvestmentOptions.FixedDeposits && (
                                    <div className="mb-5">
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            Fixed Deposits are offered by Banks/NBFCs and are
                                            regulated by the Reserve Bank of India. ETA Fintech
                                            Private Limited, under the brand name of InCred Money,
                                            is acting in the capacity of Sub Agent/Referral Partner
                                            for distribution of Fixed Deposits of Banks and NBFCs.
                                            The funds transferred by the customer for the Fixed
                                            Deposits are directly credited to the regulated entity
                                            offering the Fixed Deposit product. Fixed Deposits are
                                            offered to Resident Indians above the age of 18 who have
                                            a PAN & an Aadhaar. Interest rates are subject to
                                            change.{" "}
                                            <a
                                                href={process.env.TERMS}
                                                target="_blank"
                                                className="underline"
                                                rel="noopener noreferrer"
                                            >
                                                *T&C apply.
                                            </a>{" "}
                                        </p>
                                    </div>
                                )}
                                {(active === InvestmentOptions.Bullion ||
                                    active === InvestmentOptions.Gold) && (
                                    <div>
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            ETA Fintech Private Limited, operating under the brand
                                            name “InCred Money,“ provides a digital platform that
                                            facilitates the purchase and sale of “Digital
                                            Gold/Silver.“ The contractual relationship in relation
                                            to the Digital Gold/Silver is exclusively between the
                                            Client and MMTC-PAMP India Private Limited (“MMTC“).
                                            InCred Money acts strictly as a referral partner and
                                            technology facilitator, providing a platform to these
                                            transactions. The Client&lsquo;s use of the InCred Money
                                            platform for any Digital Gold/Silver transaction
                                            constitutes an express agreement and acknowledgement of
                                            this arrangement.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            By initiating any transaction for Digital Gold/Silver on
                                            the InCred Money platform, the Client hereby represents
                                            and warrants that they have read, understood, and
                                            unequivocally agree to be bound by the terms and
                                            conditions of service, as well as the disclaimers of
                                            representation and warranties, as stipulated under{" "}
                                            <a
                                                href="http://www.incredmoney.com/terms-of-use"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline"
                                            >
                                                incredmoney.com/terms-of-use
                                            </a>
                                            . It is the Client&lsquo;s sole responsibility to review
                                            and comprehend the agreement it signs with MMTC prior to
                                            undertaking any transaction. The initiation of a
                                            transaction shall be deemed as the Client&lsquo;s
                                            conclusive acceptance of the said terms and disclaimers.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            The Client acknowledges and agrees that all aspects of
                                            the Digital Gold/SIlver, including but not limited to its
                                            delivery, quality, quantity, purity, and custodial
                                            arrangements, are the exclusive responsibility and
                                            liability of MMTC. InCred Money shall not be held liable,
                                            at any point in time or under any circumstances, for the
                                            quality, delivery, or any other matter pertaining to the
                                            Digital Gold/SIlver transacted between the Client and
                                            MMTC. InCred Money makes no representations or
                                            warranties, express or implied, with respect to the
                                            Digital Gold/Silver offered by MMTC.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            Any complaints or grievances related to the quality,
                                            delivery, or other aspects of the Digital Gold/Silver
                                            must be addressed directly with MMTC. InCred Money may,
                                            at its sole discretion, assist in facilitating
                                            communication between the Client and MMTC as a courtesy,
                                            but is under no obligation to do so. InCred Money assumes
                                            no liability or responsibility arising from such
                                            facilitation. Resolution of any disputes or grievances
                                            is exclusively between the Client and MMTC.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            InCred Money hereby discloses that it receives
                                            remuneration from MMTC for the facilitation of Digital
                                            Gold/Silver transactions conducted through its platform,
                                            pursuant to a pre-existing commercial arrangement between
                                            InCred Money and MMTC. Investment in digital gold &
                                            silver is at your own risk & discretion. Please read the
                                            terms, conditions and disclaimers on the InCred Money &
                                            MMTC-PAMP web platform prior to any purchase.
                                        </p>
                                        <br />
                                        <p className="text-sm font-medium leading-[19.6px]">
                                            Investment in digital gold & silver is at your own risk
                                            & discretion. Please read the terms, conditions and
                                            disclaimers on the InCred Money & MMTC-PAMP web platform
                                            prior to any purchase.
                                        </p>
                                    </div>
                                )}
                                {active === InvestmentOptions.Home && disclaimerBroking}

                                <p className="mt-4 text-sm font-regular leading-5 text-neutralOpacityLight-50">
                                    Important Links:{" "}
                                    {regulatoryLinks.map((link, index) => (
                                        <React.Fragment key={link.heading}>
                                            {index > 0 ? " | " : null}
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline-offset-2 hover:underline"
                                            >
                                                {link.heading}
                                            </a>
                                        </React.Fragment>
                                    ))}
                                </p>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
